// CHAT-PRESENCE (founder's design discussion 2026-10-29): the ring on a DE's avatar. One rule for
// the API (`dePresence.list`), the web twin and anything else that draws a ring.

export type DeRingState = "needs_you" | "stuck" | "working" | "waiting" | "available" | "off";

/** The DE's lifecycle (`digital_employee.status`). */
export type DeLifecycle = "onboarding" | "active" | "paused" | "retired" | "not_deployed";

export type DeRingFacts = {
  lifecycle: DeLifecycle;
  /** An ask or approval of the DE waits on the viewer, or a thread awaits the viewer's reply. */
  needsViewer: boolean;
  /** The DE's latest finished run failed and nobody has acknowledged it. */
  failedUnacked: boolean;
  /** A run of the DE is queued or running. */
  live: boolean;
  /** A run of the DE waits (input, approval, children), not on the viewer. */
  waiting: boolean;
};

/** The ring's states, highest priority first. */
export const DE_RING_PRIORITY: readonly DeRingState[] = [
  "needs_you",
  "stuck",
  "working",
  "waiting",
  "available",
  "off",
];

/**
 * The ring of an active DE, in priority order: needs you, stuck, working, waiting on others,
 * available. A paused, onboarding, retired or not deployed (DE-FOLLOWUPS) DE is off, whatever its
 * runs (the founder's "Off: paused, onboarding or retired").
 */
export function deRingState(f: DeRingFacts): DeRingState {
  if (f.lifecycle !== "active") return "off";
  if (f.needsViewer) return "needs_you";
  if (f.failedUnacked) return "stuck";
  if (f.live) return "working";
  if (f.waiting) return "waiting";
  return "available";
}

/** The first line of a failure, at most `max` characters (the hover card's one line). */
export function firstLine(text: string | null | undefined, max = 140): string | null {
  const line = (text ?? "").split(/\r?\n/u).find((l) => l.trim().length > 0);
  if (!line) return null;
  const t = line.trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
}

/** One sentence of an ask: its first sentence or line, at most `max` characters. */
export function oneSentence(text: string | null | undefined, max = 160): string | null {
  const line = firstLine(text, Number.MAX_SAFE_INTEGER);
  if (!line) return null;
  const m = /^(.+?[.!?])(\s|$)/u.exec(line);
  const s = (m?.[1] ?? line).trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/** Days "Clean up" may close quiet DMs after. */
export const DM_CLEANUP_DAYS = [7, 14, 30] as const;

/**
 * Whether a DM is quiet enough for "Clean up": no message within `days` before `now` (a DM with
 * no message at all counts from when it was made).
 */
export function dmQuiet(
  lastMessageAt: string | Date | null,
  createdAt: string | Date,
  days: number,
  now: Date,
): boolean {
  const last = new Date(lastMessageAt ?? createdAt).getTime();
  return now.getTime() - last >= days * 86_400_000;
}

/** A DM the viewer closed stays closed until someone writes after the close. */
export function dmClosed(closedAt: string | Date | null, lastMessageAt: string | Date | null) {
  if (closedAt === null) return false;
  if (lastMessageAt === null) return true;
  return new Date(lastMessageAt).getTime() <= new Date(closedAt).getTime();
}
